import { generateKeyBetween, generateNKeysBetween } from 'fractional-indexing';

// Task order is a fractional index: a string key per task, compared byte by byte
// (tasks.position is collated "C" so Postgres agrees). Moving a task only rewrites
// that task's key, so two people reordering at once rarely clash.

/** Byte-order comparison, the same order Postgres uses for tasks.position. */
export function comparePositions(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * A key that sorts after `before` and before `after` (either may be null for an
 * end of the list). If the neighbours are out of order (two tasks were given the
 * same key at once), the key goes after `before`.
 */
export function positionBetween(before: string | null | undefined, after: string | null | undefined): string {
  const lo = before || null;
  const hi = after || null;
  if (lo && hi && lo >= hi) return generateKeyBetween(lo, null);
  return generateKeyBetween(lo, hi);
}

/** `count` keys in order, between `before` and `after`. */
export function positionsBetween(
  before: string | null | undefined,
  after: string | null | undefined,
  count: number,
): string[] {
  const lo = before || null;
  const hi = after || null;
  if (lo && hi && lo >= hi) return generateNKeysBetween(lo, null, count);
  return generateNKeysBetween(lo, hi, count);
}

/**
 * The key for a task dropped at `index` in a column whose other tasks have the
 * keys `ordered` (sorted, without the task being moved).
 */
export function positionAt(ordered: readonly string[], index: number): string {
  const i = Math.max(0, Math.min(index, ordered.length));
  return positionBetween(ordered[i - 1] ?? null, ordered[i] ?? null);
}
